import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import SvgIcon from '../SvgIcon';
import { useWindowSize } from 'react-use';
import { ArrowButtonNext, ArrowButtonPrev } from '../ArrowButton';

export interface ITabbedNavItem {
  id: number;
  title: React.ReactNode;
}

export interface ITabbedNavProps<T = ITabbedNavItem> extends Omit<React.HTMLAttributes<HTMLUListElement>, 'onChange'> {
  items?: T[];
  value?: number; // selected index
  arrows?: boolean;
  renderItem?: (item: T, index: number) => React.ReactElement;
  onChange?: (item: T, index: number) => void;
}

export function TabbedNav({
  items,
  value,
  className,
  arrows = true,
  onChange,
  renderItem,
  ...otherProps
}: ITabbedNavProps): React.ReactElement {
  const bem = bemBlock('tabbed-nav');

  const { width } = useWindowSize();

  const [listRef, setListRef] = React.useState<HTMLUListElement>();

  const pageArrowPrevDisabled = value === 0;
  const pageArrowNextDisabled = value === (items?.length || 1) - 1;

  const index = items.findIndex(item => item.id === value);

  function handlePrev() {
    const newIndex = index - 1;
    if (newIndex >= 0) {
      onChange(items[newIndex], newIndex);
    }
  }

  function handleNext() {
    const newIndex = index + 1;
    if (newIndex < items.length) {
      onChange(items[newIndex], newIndex);
    }
  }

  const handlePrevCallback = React.useCallback(handlePrev, [items, index, onChange]);
  const handleNextCallback = React.useCallback(handleNext, [items, index, onChange]);

  function handleKeyPress(ev) {
    if (ev.code === 'ArrowLeft') {
      handlePrevCallback();
    }
    if (ev.code === 'ArrowRight') {
      handleNextCallback();
    }
  }

  const handleKeyPressCallback = React.useCallback(handleKeyPress, [handleNextCallback, handlePrevCallback]);

  React.useEffect(() => {
    document.removeEventListener('keydown', handleKeyPressCallback);
    document.addEventListener('keydown', handleKeyPressCallback);
    return () => {
      document.removeEventListener('keydown', handleKeyPressCallback);
    };
  }, [value, handleKeyPressCallback]);

  React.useEffect(() => {
    if (listRef) {
      const el = listRef.childNodes[index] as HTMLLIElement;
      el?.scrollIntoView();
    }
  }, [index, listRef, width]);

  const arrowsVisible = arrows && listRef && listRef.scrollWidth > listRef.clientWidth;

  return (
    <div className={bem('wrapper', [{ ['arrows-visible']: arrowsVisible }])}>
      <ul className={bem(null, [], className)} {...otherProps} ref={newRef => setListRef(newRef)}>
        {items.map((item, i) => (
          <li
            className={bem('item', [
              {
                selected: value === item.id,
              },
            ])}
            onClick={() => onChange(item, i)}
            key={i}
          >
            {renderItem ? renderItem(item, i) : item.title}
          </li>
        ))}
      </ul>
      {arrowsVisible && (
        <ArrowButtonPrev disabled={pageArrowPrevDisabled} onClick={handlePrev} className={bem('arrow-prev')} />
      )}
      {arrowsVisible && (
        <ArrowButtonNext disabled={pageArrowNextDisabled} onClick={handleNext} className={bem('arrow-next')} />
      )}
    </div>
  );
}
